import { enrichSaleDetail, type EnrichSaleDetailOptions } from './enrichSaleDetail';
import { salesEnrichAdapter } from '../infra/salesEnrichAdapter';
import type { SaleDetail } from '../domain/salesTypes';
import { isPendingMercadoPagoQrSale } from '../../mercadopago/domain/isPendingMercadoPagoQrSale';
import { shouldBackfillMpQrOperationNumber } from '../../mercadopago/domain/mpQrSaleMetadata';
import { syncMpQrPaymentStatusRemote } from '../../mercadopago/infra/mercadopagoQrService';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { fetchRemoteSaleDetail } from '../../reports/infra/reportsRemoteSalesListRepo';

export type LoadSaleDetailOptions = EnrichSaleDetailOptions & {
  /** Evita get-sale-qr extra cuando el modal MP QR ya sincroniza en paralelo. */
  skipMpQrSync?: boolean;
};

async function refreshMpQrPaymentIfPending(saleId: string, detail: SaleDetail | null): Promise<void> {
  if (!detail || !isPendingMercadoPagoQrSale(detail)) return;
  if (!(await isOnline())) return;

  const now = Date.now();
  const last = mpQrDetailSyncAt.get(saleId) ?? 0;
  if (now - last < MP_QR_DETAIL_SYNC_THROTTLE_MS) return;
  mpQrDetailSyncAt.set(saleId, now);

  await syncMpQrPaymentStatusRemote(saleId);
}

async function backfillMpQrOperationNumberIfNeeded(saleId: string, detail: SaleDetail | null): Promise<boolean> {
  if (!detail || !shouldBackfillMpQrOperationNumber(detail)) return false;
  if (!(await isOnline())) return false;

  const now = Date.now();
  const throttleKey = `${saleId}:meta`;
  const last = mpQrDetailSyncAt.get(throttleKey) ?? 0;
  if (now - last < MP_QR_DETAIL_SYNC_THROTTLE_MS) return false;
  mpQrDetailSyncAt.set(throttleKey, now);

  await syncMpQrPaymentStatusRemote(saleId);
  return true;
}

const MP_QR_DETAIL_SYNC_THROTTLE_MS = 30_000;
const mpQrDetailSyncAt = new Map<string, number>();

/**
 * Detalle de venta — remote-only (ADR-017).
 * `salesEnrichAdapter` aporta stubs de allocations + getCustomerName.
 */
export async function loadSaleDetail(
  saleId: string,
  options?: LoadSaleDetailOptions
): Promise<SaleDetail | null> {
  const skipMpQrSync = options?.skipMpQrSync === true;
  const enrichOpts: EnrichSaleDetailOptions = {
    authUserLabels: options?.authUserLabels,
    ownerAuthUserId: options?.ownerAuthUserId,
  };

  if (!(await isOnline())) return null;

  try {
    let remote = await fetchRemoteSaleDetail(saleId);
    if (!remote) return null;

    if (!skipMpQrSync) {
      await refreshMpQrPaymentIfPending(saleId, remote);
      await backfillMpQrOperationNumberIfNeeded(saleId, remote);
      if (isPendingMercadoPagoQrSale(remote) || shouldBackfillMpQrOperationNumber(remote)) {
        remote = (await fetchRemoteSaleDetail(saleId)) ?? remote;
      }
    }

    return enrichSaleDetail(remote, salesEnrichAdapter, enrichOpts);
  } catch {
    return null;
  }
}
